import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, Download, HelpCircle, AlertCircle } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

interface RetirementInputs {
  currentAge: number;
  retirementAge: number;
  lifeExpectancy: number;
  currentSavings: number;
  monthlyContribution: number;
  expectedReturn: number;
  inflationRate: number;
  desiredIncome: number;
  statePension: number;
  otherIncome: number;
}

interface ProjectionData {
  age: number;
  savings: number;
  contributions: number;
  returns: number;
  withdrawals: number;
  balance: number;
}

export default function RetirementCalculator() {
  const [inputs, setInputs] = useState<RetirementInputs>({
    currentAge: 30,
    retirementAge: 67,
    lifeExpectancy: 90,
    currentSavings: 50000,
    monthlyContribution: 500,
    expectedReturn: 7,
    inflationRate: 2.5,
    desiredIncome: 40000,
    statePension: 10600,
    otherIncome: 0
  });

  const [showInfo, setShowInfo] = useState(false);
  const [projectionData, setProjectionData] = useState<ProjectionData[]>([]);
  const [summary, setSummary] = useState({
    totalSavingsNeeded: 0,
    monthlyContributionNeeded: 0,
    savingsShortfall: 0,
    retirementIncome: 0,
    incomeShortfall: 0
  });

  useEffect(() => {
    calculateProjection();
  }, [inputs]);

  const calculateProjection = () => {
    const {
      currentAge,
      retirementAge,
      lifeExpectancy,
      currentSavings,
      monthlyContribution,
      expectedReturn,
      inflationRate,
      desiredIncome,
      statePension,
      otherIncome
    } = inputs;

    const monthlyReturn = (1 + expectedReturn / 100) ** (1 / 12) - 1;
    const monthlyInflation = (1 + inflationRate / 100) ** (1 / 12) - 1;
    
    let balance = currentSavings;
    const projection: ProjectionData[] = [];
    let totalContributions = currentSavings;
    let totalReturns = 0;
    let totalWithdrawals = 0;

    // Calculate required savings for retirement
    const yearsInRetirement = lifeExpectancy - retirementAge;
    const monthlyIncomeNeeded = desiredIncome / 12;
    const monthlyPension = statePension / 12;
    const monthlyOtherIncome = otherIncome / 12;
    const monthlyWithdrawalNeeded = monthlyIncomeNeeded - monthlyPension - monthlyOtherIncome;

    // Project savings growth and withdrawals
    for (let age = currentAge; age <= lifeExpectancy; age++) {
      const isRetired = age >= retirementAge;
      
      for (let month = 0; month < 12; month++) {
        // Calculate returns
        const monthlyReturns = balance * monthlyReturn;
        totalReturns += monthlyReturns;
        
        // Add contributions or subtract withdrawals
        if (!isRetired) {
          balance += monthlyContribution;
          totalContributions += monthlyContribution;
        } else {
          const inflatedWithdrawal = monthlyWithdrawalNeeded * (1 + monthlyInflation) ** 
            ((age - retirementAge) * 12 + month);
          balance -= inflatedWithdrawal;
          totalWithdrawals += inflatedWithdrawal;
        }
        
        // Add investment returns
        balance += monthlyReturns;
      }

      projection.push({
        age,
        savings: totalContributions,
        contributions: totalContributions - currentSavings,
        returns: totalReturns,
        withdrawals: totalWithdrawals,
        balance: Math.max(0, balance)
      });
    }

    setProjectionData(projection);

    // Calculate summary statistics
    const finalProjection = projection[projection.length - 1];
    const totalSavingsNeeded = monthlyWithdrawalNeeded * 12 * yearsInRetirement * 
      (1 + inflationRate / 100) ** yearsInRetirement;
    
    const currentTrajectory = finalProjection.balance > 0;
    const savingsShortfall = currentTrajectory ? 0 : Math.abs(finalProjection.balance);
    
    const additionalMonthlySavingsNeeded = currentTrajectory ? 0 :
      (savingsShortfall / ((1 + expectedReturn / 100) ** (retirementAge - currentAge) - 1)) /
      (retirementAge - currentAge) / 12;

    setSummary({
      totalSavingsNeeded,
      monthlyContributionNeeded: monthlyContribution + additionalMonthlySavingsNeeded,
      savingsShortfall,
      retirementIncome: desiredIncome,
      incomeShortfall: Math.max(0, desiredIncome - (statePension + otherIncome))
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadProjection = () => {
    let csv = 'Retirement Planning Projection\n\n';
    
    // Inputs
    csv += 'Inputs\n';
    Object.entries(inputs).forEach(([key, value]) => {
      csv += `${key},${value}\n`;
    });
    
    // Summary
    csv += '\nSummary\n';
    Object.entries(summary).forEach(([key, value]) => {
      csv += `${key},${value}\n`;
    });
    
    // Projection Data
    csv += '\nYearly Projection\n';
    csv += 'Age,Savings,Contributions,Returns,Withdrawals,Balance\n';
    projectionData.forEach(row => {
      csv += `${row.age},${row.savings},${row.contributions},${row.returns},${row.withdrawals},${row.balance}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retirement-projection.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const retirementChartConfig = { balance: { label: 'Portfolio Balance', color: 'hsl(var(--chart-1))' } };

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" asChild>
            <Link to="/tools" className="inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Tools
            </Link>
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Retirement Calculator</h1>
              <p className="text-muted-foreground">Plan your retirement savings and estimate future income needs</p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-lg font-semibold">Retirement Planning</h2>
                <div className="flex items-center gap-4">
                  <Button variant="ghost" size="icon" onClick={() => setShowInfo(!showInfo)}>
                    <HelpCircle className="w-5 h-5" />
                  </Button>
                  <Button variant="secondary" size="sm" onClick={downloadProjection}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Projection
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showInfo && (
                  <div className="mb-6 p-4 bg-muted rounded-lg">
                    <h3 className="font-medium mb-2">About This Calculator</h3>
                    <p className="text-sm text-muted-foreground">
                      This calculator helps you estimate how much you need to save for retirement and whether you're on track to meet your goals. It considers factors like inflation, investment returns, and other income sources.
                    </p>
                  </div>
                )}

                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Current Age</Label>
                        <Input type="number" min={18} max={100} value={inputs.currentAge} onChange={(e) => setInputs({ ...inputs, currentAge: Number(e.target.value) })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Retirement Age</Label>
                        <Input type="number" min={inputs.currentAge} max={100} value={inputs.retirementAge} onChange={(e) => setInputs({ ...inputs, retirementAge: Number(e.target.value) })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Life Expectancy</Label>
                        <Input type="number" min={inputs.retirementAge} max={120} value={inputs.lifeExpectancy} onChange={(e) => setInputs({ ...inputs, lifeExpectancy: Number(e.target.value) })} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium mb-4">Current Savings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Current Retirement Savings</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                          <Input type="number" min={0} value={inputs.currentSavings} onChange={(e) => setInputs({ ...inputs, currentSavings: Number(e.target.value) })} className="pl-8" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly Contribution</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                          <Input type="number" min={0} value={inputs.monthlyContribution} onChange={(e) => setInputs({ ...inputs, monthlyContribution: Number(e.target.value) })} className="pl-8" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium mb-4">Investment Assumptions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Expected Annual Return</Label>
                        <div className="relative">
                          <Input type="number" min={0} max={20} step={0.1} value={inputs.expectedReturn} onChange={(e) => setInputs({ ...inputs, expectedReturn: Number(e.target.value) })} className="pr-8" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Expected Inflation Rate</Label>
                        <div className="relative">
                          <Input type="number" min={0} max={10} step={0.1} value={inputs.inflationRate} onChange={(e) => setInputs({ ...inputs, inflationRate: Number(e.target.value) })} className="pr-8" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium mb-4">Retirement Income</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Desired Annual Income</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                          <Input type="number" min={0} value={inputs.desiredIncome} onChange={(e) => setInputs({ ...inputs, desiredIncome: Number(e.target.value) })} className="pl-8" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>State Pension</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                          <Input type="number" min={0} value={inputs.statePension} onChange={(e) => setInputs({ ...inputs, statePension: Number(e.target.value) })} className="pl-8" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Other Income</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                          <Input type="number" min={0} value={inputs.otherIncome} onChange={(e) => setInputs({ ...inputs, otherIncome: Number(e.target.value) })} className="pl-8" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Retirement Savings Projection</h2>
              </CardHeader>
              <CardContent>
                <ChartContainer config={retirementChartConfig} className="h-[400px] w-full">
                  <AreaChart data={projectionData}>
                    <defs>
                      <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-balance)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-balance)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="age" tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} labelFormatter={(label) => `Age ${label}`} />} />
                    <Area type="monotone" dataKey="balance" name="Portfolio Balance" stroke="var(--color-balance)" fill="url(#colorBalance)" strokeWidth={2} />
                  </AreaChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Results Summary</h2>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Savings Needed</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.totalSavingsNeeded)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Monthly Savings Needed</p>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(summary.monthlyContributionNeeded)}</p>
                  {summary.monthlyContributionNeeded > inputs.monthlyContribution && (
                    <p className="text-sm text-destructive mt-1">
                      Increase monthly savings by {formatCurrency(summary.monthlyContributionNeeded - inputs.monthlyContribution)}
                    </p>
                  )}
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Expected Retirement Income</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">State Pension</span>
                      <span className="font-medium">{formatCurrency(inputs.statePension)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Other Income</span>
                      <span className="font-medium">{formatCurrency(inputs.otherIncome)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">From Savings</span>
                      <span className="font-medium">{formatCurrency(summary.incomeShortfall)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="font-medium">Total</span>
                      <span className="font-medium">{formatCurrency(summary.retirementIncome)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-2">Planning Tips</h3>
                    <ul className="text-sm text-muted-foreground space-y-2">
                      <li>Start saving early to benefit from compound interest</li>
                      <li>Consider increasing contributions with salary raises</li>
                      <li>Review and rebalance your investment portfolio regularly</li>
                      <li>Account for healthcare costs in retirement</li>
                      <li>Consider consulting with a financial advisor</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}